import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const BASE_URL = "https://slotify.nl";
const SUPPORTED_LANGS = ["en", "nl", "fr", "de"] as const;

/**
 * Injects canonical, hreflang, and SiteNavigationElement structured data
 * into <head> for the current page.
 */
const SEOHead = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const cleanPath = pathname.replace(/\/+$/, "") || "";
    const canonicalUrl = `${BASE_URL}${cleanPath}`;

    // --- Canonical ---
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical) {
      canonical.href = canonicalUrl;
    } else {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      canonical.href = canonicalUrl;
      document.head.appendChild(canonical);
    }

    // --- Hreflang tags ---
    // Remove old hreflang tags we previously injected
    document.querySelectorAll('link[data-seo-hreflang]').forEach((el) => el.remove());

    SUPPORTED_LANGS.forEach((lang) => {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = lang;
      link.href = canonicalUrl;
      link.setAttribute("data-seo-hreflang", "true");
      document.head.appendChild(link);
    });

    // x-default hreflang
    const xDefault = document.createElement("link");
    xDefault.rel = "alternate";
    xDefault.hreflang = "x-default";
    xDefault.href = canonicalUrl;
    xDefault.setAttribute("data-seo-hreflang", "true");
    document.head.appendChild(xDefault);

    return () => {
      document.querySelectorAll('link[data-seo-hreflang]').forEach((el) => el.remove());
    };
  }, [pathname]);

  // --- SiteNavigationElement JSON-LD ---
  useEffect(() => {
    const id = "seo-site-navigation-ld";
    if (document.getElementById(id)) return;

    const script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": [
        {
          "@type": "SiteNavigationElement",
          "position": 1,
          "name": "Features",
          "url": `${BASE_URL}/#features`,
        },
        {
          "@type": "SiteNavigationElement",
          "position": 2,
          "name": "Extensions",
          "url": `${BASE_URL}/extensions/self-service`,
        },
        {
          "@type": "SiteNavigationElement",
          "position": 3,
          "name": "How It Works",
          "url": `${BASE_URL}/#how-it-works`,
        },
        {
          "@type": "SiteNavigationElement",
          "position": 4,
          "name": "Pricing",
          "url": `${BASE_URL}/#pricing`,
        },
        {
          "@type": "SiteNavigationElement",
          "position": 5,
          "name": "Self-Service Extension",
          "url": `${BASE_URL}/extensions/self-service`,
        },
        {
          "@type": "SiteNavigationElement",
          "position": 6,
          "name": "Statistics Extension",
          "url": `${BASE_URL}/extensions/statistics`,
        },
        {
          "@type": "SiteNavigationElement",
          "position": 7,
          "name": "Pay at Order Extension",
          "url": `${BASE_URL}/extensions/pay-at-order`,
        },
        {
          "@type": "SiteNavigationElement",
          "position": 8,
          "name": "Login",
          "url": `${BASE_URL}/login`,
        },
        {
          "@type": "SiteNavigationElement",
          "position": 9,
          "name": "Sign Up",
          "url": `${BASE_URL}/signup`,
        },
      ],
    });
    document.head.appendChild(script);

    return () => {
      document.getElementById(id)?.remove();
    };
  }, []);

  return null;
};

export default SEOHead;
